import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MarketContext } from "../../contexts/MarketContext";
import { findTradingTypeByInstrument } from "../../config/features";
import { getAssetName, getAssetShortName } from "../../utils/asset/assets";
import { Chevron } from "../shared/Chevron/style";
import TickerLogo from "../shared/TickerLogo";
import {
  CommandKeyChevronContainer,
  HeaderText,
  MarketsDropdownWrapper,
  SubText,
} from "./style";
import { Command } from "@phosphor-icons/react";

interface IActiveAsset {
  asset: string | undefined;
  title: string | undefined;
  isPrelaunch: boolean;
}

function MarketSelectionButton({ isMobile }: { isMobile?: boolean }) {
  const { market, showCommandModal, setShowCommandModal } =
    useContext(MarketContext);
  // Vertical name comes from the shared `tradingTypes` block so the button, the
  // market picker and the portfolio filter all read the same label.
  const { t: tTradingType } = useTranslation("app", {
    keyPrefix: "tradingTypes",
  });

  const isMacLike = /(Mac)/i.test(navigator.userAgent);

  // The quote suffix ("-USD", "-USDC", none) is declared per vertical in the
  // trading-type registry rather than branched on here. `market.derivative` can
  // come from an API row (see Orders/Positions), so an unrecognised value falls
  // back to the plain asset name instead of crashing the trading header.
  const tradingType = findTradingTypeByInstrument(market.derivative);

  const activeAsset: IActiveAsset = useMemo(
    () => ({
      asset: market.asset,
      title: tradingType?.quoteLabelSuffix
        ? `${getAssetShortName(market.asset) || market.asset}${tradingType.quoteLabelSuffix}`
        : getAssetName(market.asset),
      isPrelaunch: false,
    }),
    [market, tradingType]
  );

  const toggleShowCommandModal = useCallback(() => {
    setShowCommandModal((v) => !v);
  }, [setShowCommandModal]);

  return (
    <MarketsDropdownWrapper onClick={toggleShowCommandModal}>
      <div>
        <TickerLogo asset={activeAsset?.asset} size={32} />
        <div>
          <HeaderText>{activeAsset?.title}</HeaderText>
          <SubText>{tradingType ? tTradingType(tradingType.labelKey) : ""}</SubText>
        </div>
      </div>
      <CommandKeyChevronContainer>
        {!isMobile && (
          <>
            <div>{isMacLike ? <Command  /> : <span>CTRL</span>}</div>
            <div>K</div>
          </>
        )}
        <Chevron
          style={
            isMobile
              ? {
                  opacity: 1,
                }
              : undefined
          }
          direction={showCommandModal ? "up" : "down"}
        />
      </CommandKeyChevronContainer>
    </MarketsDropdownWrapper>
  );
}

export default MarketSelectionButton;
